// English — the platform's reference dictionary: every string the shared shell
// needs (sign-in, pricing, paywall, checkout, billing, team, language). A
// product merges its own sections on top with mergeDictionaries(); sections
// with the same name (auth, nav, settings, pricing) are merged key by key, so
// a product can also override a line here, such as the sign-in hero copy.
//
// `{brand}` is filled once per product by withBrand(); the other placeholders
// ({n}, {email}) are filled at render time with fmt() from useI18n().

export const coreEn = {
  brand: {
    name: '{brand}',
  },
  legal: {
    agree: 'By continuing you agree to the',
    terms: 'Terms of service',
    and: 'and the',
    privacy: 'Privacy policy',
  },
  language: {
    title: 'Language',
    hint: 'Menus, dates and numbers. Your choice only — teammates pick their own.',
    switch: 'Language',
    en: 'English',
    pt: 'Português',
  },
  time: {
    justNow: 'just now',
    minutesAgo: '{n}m ago',
    hoursAgo: '{n}h ago',
    daysAgo: '{n}d ago',
  },
  common: {
    loading: 'Loading…',
    saving: 'Saving…',
    allSaved: 'All changes saved',
    saveFailed: 'Could not save. Your last edit was not stored.',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    confirmDelete: 'Delete for good?',
    retry: 'Try again',
    search: 'Search',
    clear: 'Clear',
    none: 'None',
    error: 'Something went wrong. Please try again.',
    notFound: 'Not found',
    back: 'Back',
    optional: 'optional',
  },
  auth: {
    signIn: 'Sign in',
    signInSubtitle: 'Use the email address you signed up with.',
    email: 'Email',
    emailPlaceholder: 'you@company.com',
    sendMagicLink: 'Email me a sign-in link',
    magicLinkSent: 'Check {email} for a sign-in link. It expires in an hour — request another if it does.',
    backToSignIn: 'Use a different email',
    orSeparator: 'or',
    signInWithGoogle: 'Continue with Google',
    signOut: 'Sign out',
    noAccount: "Don't have an account yet?",
    viewPlans: 'See pricing',
    forbidden: "You don't have access to this page.",
    errors: {
      otp_disabled: 'Email sign-in is turned off for this workspace. Use Google instead.',
      over_email_send_rate_limit: 'Too many sign-in emails in a row. Wait a minute and try again.',
      signup_disabled: 'No account uses that email. Check the address, or see pricing to start one.',
      validation_failed: 'That does not look like an email address.',
      default: 'Could not sign you in. Please try again.',
    },
    heroTitle: 'Welcome to {brand}.',
    heroSubline: 'Sign in to pick up where you left off.',
  },
  pricing: {
    title: 'Try {brand} free for {n} days',
    intervalLabel: 'Billing period',
    monthly: 'Monthly',
    yearly: 'Yearly',
    monthsFree: '{n} months free',
    thenMonthly: 'Then {price} per month.',
    thenYearly: 'Then {price} per year, about {perMonth} per month.',
    trialKicker: 'Free trial',
    trialHeadline: '{n} days free',
    selectPlan: 'Start your {n}-day free trial',
    submitting: 'Opening secure checkout…',
    loadError: 'Could not load pricing. Refresh and try again.',
    checkoutError: 'Could not open checkout. Please try again.',
    notConfigured: 'Billing is not configured yet. Run `pnpm seed:plan` to sync the Stripe catalog.',
    alreadyHaveAccount: 'Already have an account?',
    whatYouGet: 'What you get',
  },
  checkout: {
    successTitle: "You're all set",
    successBody: 'Your workspace is being created. We have emailed you a sign-in link — or just sign in below.',
    successHint: 'Provisioning normally takes a few seconds.',
    goToSignIn: 'Go to sign in',
    cancelTitle: 'Checkout cancelled',
    cancelBody: 'Nothing was charged. You can pick a plan again whenever you are ready.',
    backToPricing: 'Back to pricing',
  },
  paywall: {
    title: 'Your subscription is not active',
    body: 'Pick a plan to get back into your workspace. Your data is untouched.',
    recoverableTitle: 'There is a problem with your payment',
    recoverableBody: 'Your last payment did not go through. Update your card and access is restored as soon as Stripe retries.',
    updatePayment: 'Update payment method',
    orStartNew: 'Or start a fresh subscription',
    pendingTitle: 'Your workspace is not ready yet',
    pendingBody: 'Checkout did not finish, or provisioning is still running. If you just paid, wait a moment and refresh.',
    memberBody: 'Only an admin of your workspace can change billing. Ask them to update the subscription.',
    signedInAs: 'Signed in as',
    portalError: 'Could not open the billing portal. Please try again.',
  },
  nav: {
    home: 'Overview',
    settings: 'Settings',
  },
  settings: {
    title: 'Settings',
    team: 'Team',
    teamHint: 'People with access to this workspace.',
    roleAdmin: 'Admin',
    roleMember: 'Member',
    inviteHint: 'Adding teammates is not self-serve yet — see docs/OPEN-QUESTIONS.md. Ask the operator to add them.',
    adminOnly: 'Only admins can change these settings.',
    saved: 'Saved',
    billing: 'Billing',
    billingHint: 'Invoices, payment method and cancellation are handled by Stripe.',
    billingPlan: 'Plan',
    billingStatus: 'Status',
    billingRenews: 'Renews on',
    billingEnds: 'Access ends on',
    billingTrialEnds: 'Trial ends on',
    billingCancelScheduled: 'Cancellation scheduled',
    billingNone: 'No subscription on record for this workspace.',
    manageBilling: 'Manage billing',
    openingPortal: 'Opening Stripe…',
    portalError: 'Could not open the billing portal. Please try again.',
    subscriptionStatus: {
      active: 'Active',
      trialing: 'Trial',
      past_due: 'Payment overdue',
      unpaid: 'Unpaid',
      canceled: 'Cancelled',
      incomplete: 'Incomplete',
      unknown: 'Unknown',
    },
  },
} as const

// `as const` gives literal value types, which a translation could never
// satisfy. Widen every leaf to string so other dictionaries can be typed
// against this one and the compiler checks that no key is missing or extra.
export type Widen<T> = T extends string ? string : { readonly [K in keyof T]: Widen<T[K]> }

export type CoreStrings = Widen<typeof coreEn>
